import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { db } from "@/lib/supabase";
import type { Database } from "@/types/database.types";
import { useLoggedInEmployee } from "@/hooks/useLoggedInEmployee";
import { useQueryParams } from "@/hooks/useQueryParams";
import { useRealtimeRefresh } from "@/hooks/useRealtimeRefresh";

export type Office = Database["public"]["Tables"]["offices"]["Row"];

export interface UseOfficesResult {
  offices: Office[];
  selectedOffices: string[];
  selectOffice: (office: string) => void;
  clearSelectedOffices: () => void;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const OfficesContext = createContext<UseOfficesResult>({
  offices: [],
  selectedOffices: [],
  selectOffice: () => { },
  clearSelectedOffices: () => { },
  loading: true,
  error: null,
  refresh: async () => { },
});

async function fetchActiveOffices(): Promise<{ offices: Office[]; error: string | null }> {
  const { data, error } = await db.offices.getActive();
  if (error) return { offices: [], error: error.message ?? "Failed to load offices" };
  return { offices: data ?? [], error: null };
}

export function OfficesProvider({ children }: { children: React.ReactNode }) {
  const { employee, loading: employeeLoading } = useLoggedInEmployee();
  const { get, setParam } = useQueryParams();
  const [offices, setOffices] = useState<Office[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get selected offices from URL query param
  const officeParam = get("office");
  const selectedOffices = useMemo(() => {
    if (officeParam) {
      return officeParam.split(",").filter(Boolean);
    }
    return [];
  }, [officeParam]);

  const refresh = useCallback(async () => {
    setLoading(true);
    const result = await fetchActiveOffices();
    setOffices(result.offices);
    setError(result.error);
    setLoading(false);
  }, []);

  const selectOffice = useCallback((office: string) => {
    const current = selectedOffices;
    if (current.includes(office)) {
      const updated = current.filter((o) => o !== office);
      setParam("office", updated.length > 0 ? updated : "all");
    } else {
      const updated = current.filter((o) => o !== "all");
      setParam("office", [...updated, office]);
    }
  }, [selectedOffices, setParam]);

  const clearSelectedOffices = useCallback(() => {
    setParam("office", "all");
  }, [setParam]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Refetch offices when any row changes remotely. List is small enough that a non-silent
  // refresh doesn't visibly flicker.
  useRealtimeRefresh(["offices"], refresh);

  // Set the selected office to the employee's office when employee data loads
  // Only do this if there's no office filter already set in the URL
  useEffect(() => {
    if (!employeeLoading && employee?.office_id && !officeParam) {
      setParam("office", [employee.office_id], { replace: true });
    }
  }, [employeeLoading, employee]);

  const value = useMemo<UseOfficesResult>(() => {
    return { offices, selectedOffices, selectOffice, clearSelectedOffices, loading, error, refresh };
  }, [offices, selectedOffices, selectOffice, clearSelectedOffices, loading, error, refresh]);

  return <OfficesContext.Provider value={value}>{children}</OfficesContext.Provider>;
}

export function useOffices(): UseOfficesResult {
  return useContext(OfficesContext);
}
